import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { navLinks, email } from '@config';
import { KEY_CODES } from '@utils';
import { useOnClickOutside } from '@hooks';

const StyledMenu = styled.div`
  display: none;

  @media (max-width: 860px) {
    display: block;
  }
`;

const StyledHamburgerButton = styled.button`
  display: none;

  @media (max-width: 860px) {
    ${({ theme }) => theme.mixins.flexCenter};
    position: relative;
    z-index: 40;
    margin-right: -12px;
    padding: 14px;
    border: 0;
    background-color: transparent;
    color: inherit;
    transition: opacity 0.15s linear;
  }

  .ham-box {
    display: inline-block;
    position: relative;
    width: var(--hamburger-width);
    height: 20px;
  }

  .ham-box-inner {
    position: absolute;
    top: 50%;
    right: 0;
    width: var(--hamburger-width);
    height: 2px;
    border-radius: 4px;
    background-color: ${({ $solid }) => ($solid ? 'var(--text)' : '#ffffff')};
    transition-duration: 0.22s;
    transition-property: transform;
    transition-delay: ${props => (props.$open ? `0.12s` : `0s`)};
    transform: rotate(${props => (props.$open ? `225deg` : `0deg`)});
    transition-timing-function: cubic-bezier(
      ${props => (props.$open ? `0.215, 0.61, 0.355, 1` : `0.55, 0.055, 0.675, 0.19`)}
    );

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      left: auto;
      right: 0;
      width: var(--hamburger-width);
      height: 2px;
      border-radius: 4px;
      background-color: ${({ $solid }) => ($solid ? 'var(--text)' : '#ffffff')};
      transition-timing-function: ease;
      transition-duration: 0.15s;
      transition-property: transform;
    }
    &:before {
      width: ${props => (props.$open ? `100%` : `120%`)};
      top: ${props => (props.$open ? `0` : `-8px`)};
      opacity: ${props => (props.$open ? 0 : 1)};
      transition: ${({ $open }) => ($open ? 'var(--ham-before-active)' : 'var(--ham-before)')};
    }
    &:after {
      width: ${props => (props.$open ? `100%` : `80%`)};
      bottom: ${props => (props.$open ? `0` : `-8px`)};
      transform: rotate(${props => (props.$open ? `-90deg` : `0`)});
      transition: ${({ $open }) => ($open ? 'var(--ham-after-active)' : 'var(--ham-after)')};
    }
  }
`;

const StyledSidebar = styled.aside`
  display: none;

  @media (max-width: 860px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    bottom: 0;
    right: 0;
    padding: 96px 28px 40px;
    width: min(84vw, 380px);
    height: 100vh;
    outline: 0;
    background-color: var(--paper);
    box-shadow: -16px 0 50px -30px rgba(11, 18, 32, 0.55);
    z-index: 35;
    transform: translateX(${props => (props.$open ? 0 : 100)}%);
    visibility: ${props => (props.$open ? 'visible' : 'hidden')};
    transition: transform 0.3s var(--easing), visibility 0.3s;
  }

  nav {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
  }

  ul {
    ${({ theme }) => theme.mixins.resetList};
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 22px;

    a {
      display: block;
      padding: 14px 12px;
      border-radius: var(--radius-sm);
      color: var(--text);
      font-size: var(--fz-xxl);
      font-weight: 500;

      &:hover,
      &:focus {
        color: var(--blue);
        background: var(--paper-tint);
      }
    }
  }

  .menu-cta {
    ${({ theme }) => theme.mixins.primaryButton};
    width: 100%;
  }

  .menu-mail {
    display: block;
    margin-top: auto;
    padding-top: 24px;
    border-top: 1px solid var(--line);
    color: var(--text-mute);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    word-break: break-all;
  }
`;

const Menu = ({ solid }) => {
  const [open, setOpen] = useState(false);

  const buttonRef = useRef(null);
  const navRef = useRef(null);
  const wrapperRef = useRef(null);

  useOnClickOutside(wrapperRef, () => setOpen(false));

  const onKeyDown = e => {
    if (e.key === KEY_CODES.ESCAPE || e.key === KEY_CODES.ESCAPE_IE11) {
      if (open) {
        setOpen(false);
        buttonRef.current && buttonRef.current.focus();
      }
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <StyledMenu>
      <Helmet>
        <body className={open ? 'blur' : ''} />
      </Helmet>

      <div ref={wrapperRef}>
        <StyledHamburgerButton
          $open={open}
          $solid={solid}
          onClick={() => setOpen(v => !v)}
          ref={buttonRef}
          aria-label={open ? 'Закрыть меню' : 'Открыть меню'}
          aria-expanded={open}>
          <span className="ham-box">
            <span className="ham-box-inner" />
          </span>
        </StyledHamburgerButton>

        <StyledSidebar $open={open} aria-hidden={!open} tabIndex={open ? 0 : -1}>
          <nav ref={navRef} aria-label="Мобильное меню">
            <ul>
              {navLinks.map(({ url, name }) => (
                <li key={url}>
                  <Link to={url} onClick={() => setOpen(false)}>
                    {name}
                  </Link>
                </li>
              ))}
            </ul>

            <Link className="menu-cta" to="/contacts" onClick={() => setOpen(false)}>
              Обсудить проект
            </Link>

            <span className="menu-mail">{email}</span>
          </nav>
        </StyledSidebar>
      </div>
    </StyledMenu>
  );
};

Menu.propTypes = {
  solid: PropTypes.bool,
};

export default Menu;
